import React, { useEffect, useMemo, useState } from 'react';
import { debounce, noop } from 'lodash';
import { useField, useForm } from 'react-final-form';
import PropTypes from 'prop-types';
import { Box, CircularProgress, MenuItem } from '@mui/material';

import TextField from 'common/forms/TextField';
import SelectField from 'common/forms/SelectField';
import { validateNDC } from 'common/forms/formValidations';
import { getCatalogDrugProfileByNdcApi, listCatalogApi } from 'api/drug';
import titleCase from 'common/utils/titleCase';

const NdcInputWithFormulary = ({ required }) => {
  const form = useForm();
  const {
    input: { value: ndcValue },
  } = useField('ndc', { subscription: { value: true } });
  const [loading, setLoading] = useState(false);
  const [formularyList, setFormularyList] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [loadingFormularyList, setLoadingFormularyList] = useState(false);

  const loadFormularyList = useMemo(() => {
    return () => {
      setLoadingFormularyList(true);
      listCatalogApi()
        .then((response) => {
          const flattenedList = [];
          if (response?.results) {
            response.results.forEach((drug) => {
              if (drug.variants && drug.variants.length > 0) {
                drug.variants.forEach((variant) => {
                  flattenedList.push({
                    drugName: drug.drugName,
                    strength: variant.strength,
                    catalogId: variant.catalogId,
                    isActive: variant.isActive,
                    isAvailable: variant.isAvailable,
                    whitelisted: variant.whitelisted,
                    displayName: variant.displayName,
                  });
                });
              }
            });
          }
          setFormularyList(flattenedList);
        })
        .catch(noop)
        .finally(() => {
          setLoadingFormularyList(false);
        });
    };
  }, []);
  const debouncedSearchByNdc = useMemo(() => {
    const searchFunc = (ndc) => {
      if (!ndc || ndc.trim().length === 0) {
        setShowDropdown(false);
        setFormularyList([]);
        return;
      }
      form.change('catalogId', undefined);
      setLoading(true);
      getCatalogDrugProfileByNdcApi(ndc)
        .then((response) => {
          if (response?.results && response.results.length > 0) {
            const formularyProfile = response.results[0];
            setShowDropdown(false);
            form.batch(() => {
              form.change('ndc', formularyProfile.ndc);
              if (formularyProfile.catalogId) {
                form.change('catalogId', formularyProfile.catalogId);
              }
            });
          } else {
            form.change('catalogId', undefined);
            setShowDropdown(true);
            loadFormularyList();
          }
        })
        .catch((error) => {
          form.change('catalogId', undefined);
          setShowDropdown(true);
          loadFormularyList();
          noop(error);
        })
        .finally(() => {
          setLoading(false);
        });
    };

    return debounce(searchFunc, 500, { trailing: true, leading: false });
  }, [form, loadFormularyList]);

  const handleFormularySelect = (e) => {
    const selectedFormularyId = e.target.value;
    const selectedFormulary = formularyList.find((item) => item.catalogId === selectedFormularyId);

    if (selectedFormulary) {
      form.batch(() => {
        if (selectedFormulary.catalogId) form.change('catalogId', selectedFormulary.catalogId);
      });
    }
  };

  useEffect(() => {
    if (showDropdown && formularyList.length === 0 && !loadingFormularyList) {
      loadFormularyList();
    }
  }, [showDropdown, formularyList.length, loadFormularyList, loadingFormularyList]);

  useEffect(() => {
    if (ndcValue && ndcValue.trim().length > 0) {
      debouncedSearchByNdc(ndcValue);
    } else {
      setShowDropdown(false);
    }
  }, [ndcValue, debouncedSearchByNdc]);

  return (
    <Box>
      <TextField
        id='Create-Drug-Profile-ndc-input'
        label='NDC'
        name='ndc'
        required={required}
        validations={[validateNDC()]}
        endAdornment={loading ? <CircularProgress size={20} /> : undefined}
      />
      {showDropdown && (
        <SelectField
          id='Create-Drug-Profile-formulary-dropdown'
          label='Select from Catalog'
          name='catalogId'
          onChange={handleFormularySelect}
          required={showDropdown}
        >
          {loadingFormularyList ? (
            <MenuItem disabled>
              <CircularProgress size={20} />
            </MenuItem>
          ) : (
            formularyList.map((item) => (
              <MenuItem key={item.catalogId} value={item.catalogId}>
                {titleCase(item.displayName)}
              </MenuItem>
            ))
          )}
        </SelectField>
      )}
    </Box>
  );
};

NdcInputWithFormulary.propTypes = {
  required: PropTypes.bool,
};

NdcInputWithFormulary.defaultProps = {
  required: true,
};

export default NdcInputWithFormulary;
